import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useLocation, useNavigate, useParams } from "react-router-dom";

import { LoaderCircle, RefreshCw, VideoOff } from "lucide-react";

import Button from "../../../../components/buttons/Button";
import EmptyState from "../../../../components/feedback/EmptyState";
import PopThumbnailCard from "../../../../components/cards/pops/PopThumbnailCard";

import { useFeedPops } from "../hooks/useFeedPops";
import { useUserSavedPops } from "../../users/saved/hooks/useUserSavedPops";
import { useUserPopsViews } from "../../users/views/hooks/useUserPopsViews";

import "./FeedPopsView.css";

const LIMIT = 10;
const MIN_WATCH_SECONDS = 3;
const RENDER_DISTANCE = 1;
const MAX_VIEW_IDS_PER_REQUEST = 50;

const normalizePopId = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const id = Number(value);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const normalizeCount = (value, fallback = 0) => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  const count = Number(value);

  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

const normalizeViewResult = (result) => {
  const popId = normalizePopId(result?.popId ?? result?.pop_id);

  if (!popId) return null;

  const realViews = normalizeCount(
    result.realViews ?? result.real_views ?? result.views,
  );

  const demoViews = normalizeCount(result.demoViews ?? result.demo_views);

  return {
    popId,
    realViews,
    demoViews,
    totalViews: normalizeCount(
      result.totalViews ?? result.total_views,
      realViews + demoViews,
    ),
  };
};

const createFeedSeed = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export default function FeedPopsView() {
  const { popId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const initialPopIdRef = useRef(normalizePopId(popId));
  const feedSeedRef = useRef(null);

  if (!feedSeedRef.current) {
    feedSeedRef.current = createFeedSeed();
  }

  const initialPopId = initialPopIdRef.current;

  const {
    pops,
    loading,
    loadingMore,
    error,
    hasMore,
    activePopId,
    loadMore,
    refreshFeed,
    setActivePop,
  } = useFeedPops({
    limit: LIMIT,
    initialPopId,
    seed: feedSeedRef.current,
  });

  const {
    isPopSaved,
    isPopSaving,
    hasSavedStatus,
    getPopSavedStatus,
    toggleSavePop,
  } = useUserSavedPops();

  const { recordPopView, getPopViewCounts, getPopViewCount } =
    useUserPopsViews();

  const [activeIndex, setActiveIndex] = useState(0);
  const [initialized, setInitialized] = useState(false);
  const [muted, setMuted] = useState(true);
  const [viewCounts, setViewCounts] = useState({});

  const containerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const activeIndexRef = useRef(0);
  const scrollFrameRef = useRef(null);
  const loadMorePendingRef = useRef(false);

  const mountedRef = useRef(false);
  const generationRef = useRef(0);

  const loadedViewIdsRef = useRef(new Set());
  const pendingViewIdsRef = useRef(new Set());
  const pendingSavedIdsRef = useRef(new Set());

  const trackedViewsRef = useRef(new Set());
  const pendingRecordedViewsRef = useRef(new Set());
  const watchProgressRef = useRef(new Map());

  const callbacksRef = useRef({});

  callbacksRef.current = {
    loadMore,
    getPopViewCounts,
    getPopSavedStatus,
    hasSavedStatus,
    recordPopView,
    setActivePop,
  };

  const popItems = useMemo(() => {
    const seen = new Set();

    return (Array.isArray(pops) ? pops : []).filter((pop) => {
      const id = normalizePopId(pop?.popId);

      if (!id || seen.has(id)) return false;

      seen.add(id);
      return true;
    });
  }, [pops]);

  const activePop = popItems[activeIndex] || null;
  const activeId = activePop?.popId ?? null;

  const nearbyPops = useMemo(() => {
    const start = Math.max(0, activeIndex - RENDER_DISTANCE);
    const end = Math.min(popItems.length, activeIndex + RENDER_DISTANCE + 1);

    return popItems.slice(start, end);
  }, [popItems, activeIndex]);

  const nearbyIds = useMemo(
    () => nearbyPops.map((pop) => pop.popId),
    [nearbyPops],
  );

  const errorMessage = error
    ? typeof error === "string"
      ? error
      : error.message || "Unable to load Pops."
    : "";

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      generationRef.current += 1;

      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container || !popItems.length || loading || initialized) {
      return;
    }

    const selectedIndex = Math.max(
      0,
      popItems.findIndex((pop) => Number(pop.popId) === initialPopId),
    );

    const target = container.querySelector(
      `[data-pop-index="${selectedIndex}"]`,
    );

    if (!target) return;

    container.scrollTop = target.offsetTop - container.offsetTop;

    activeIndexRef.current = selectedIndex;
    setActiveIndex(selectedIndex);
    setInitialized(true);
  }, [popItems, initialPopId, initialized, loading]);

  useEffect(() => {
    if (!initialized || !popItems.length) return;

    if (activeIndexRef.current >= popItems.length) {
      const index = popItems.length - 1;

      activeIndexRef.current = index;
      setActiveIndex(index);
    }
  }, [popItems.length, initialized]);

  useEffect(() => {
    if (!initialized || !activeId) return;

    callbacksRef.current.setActivePop(activeId);

    const nextPath = `/pops/${activeId}`;

    if (location.pathname !== nextPath) {
      navigate(
        {
          pathname: nextPath,
          search: location.search,
        },
        {
          replace: true,
          preventScrollReset: true,
          state: location.state,
        },
      );
    }
  }, [
    initialized,
    activeId,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    if (!initialized || !popItems.length) return;

    const container = containerRef.current;

    if (!container) return;

    const handleScroll = () => {
      if (scrollFrameRef.current !== null) return;

      scrollFrameRef.current = requestAnimationFrame(() => {
        scrollFrameRef.current = null;

        const height = container.clientHeight;

        if (!height) return;

        const index = Math.max(
          0,
          Math.min(
            popItems.length - 1,
            Math.round(container.scrollTop / height),
          ),
        );

        if (index === activeIndexRef.current) return;

        activeIndexRef.current = index;
        setActiveIndex(index);
      });
    };

    container.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      container.removeEventListener("scroll", handleScroll);

      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }
    };
  }, [initialized, popItems.length]);

  useEffect(() => {
    if (!nearbyIds.length) return;

    const generation = generationRef.current;

    const missingIds = [...new Set(nearbyIds)].filter((id) => {
      const key = String(id);

      return (
        !loadedViewIdsRef.current.has(key) &&
        !pendingViewIdsRef.current.has(key)
      );
    });

    for (
      let index = 0;
      index < missingIds.length;
      index += MAX_VIEW_IDS_PER_REQUEST
    ) {
      const batch = missingIds.slice(index, index + MAX_VIEW_IDS_PER_REQUEST);

      batch.forEach((id) => {
        pendingViewIdsRef.current.add(String(id));
      });

      Promise.resolve()
        .then(() => callbacksRef.current.getPopViewCounts(batch))
        .then((results) => {
          if (
            !mountedRef.current ||
            generation !== generationRef.current ||
            !Array.isArray(results)
          ) {
            return;
          }

          const nextCounts = {};

          results.forEach((result) => {
            const normalized = normalizeViewResult(result);

            if (!normalized) return;

            const key = String(normalized.popId);

            nextCounts[key] = normalized;
            loadedViewIdsRef.current.add(key);
          });

          if (Object.keys(nextCounts).length) {
            setViewCounts((previous) => ({
              ...previous,
              ...nextCounts,
            }));
          }
        })
        .catch((viewError) => {
          console.error("Unable to retrieve Pop view counts:", viewError);
        })
        .finally(() => {
          batch.forEach((id) => {
            pendingViewIdsRef.current.delete(String(id));
          });
        });
    }
  }, [nearbyIds]);

  useEffect(() => {
    nearbyIds.forEach((id) => {
      const key = String(id);

      if (
        callbacksRef.current.hasSavedStatus(id) ||
        pendingSavedIdsRef.current.has(key)
      ) {
        return;
      }

      pendingSavedIdsRef.current.add(key);

      Promise.resolve()
        .then(() => callbacksRef.current.getPopSavedStatus(id))
        .catch((savedError) => {
          console.error("Unable to retrieve Pop saved status:", savedError);
        })
        .finally(() => {
          pendingSavedIdsRef.current.delete(key);
        });
    });
  }, [nearbyIds]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !initialized) return;

    const videos = container.querySelectorAll("video");

    videos.forEach((video) => {
      const item = video.closest(".user-pops-feed__item");
      const index = Number(item?.dataset.popIndex);

      const isActive = index === activeIndex;

      video.muted = muted;
      video.playsInline = true;

      if (!isActive || document.hidden) {
        video.pause();
        return;
      }

      const play = async () => {
        try {
          if (video.readyState === 0) {
            video.load();
          }

          await video.play();
        } catch (playError) {
          if (playError?.name === "AbortError") return;

          if (!video.muted) {
            video.muted = true;

            try {
              await video.play();
            } catch (retryError) {
              if (retryError?.name !== "AbortError") {
                console.warn("Pop playback failed:", retryError);
              }
            }
          }
        }
      };

      void play();
    });
  }, [activeIndex, muted, nearbyPops, initialized]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const container = containerRef.current;

      if (!container) return;

      const videos = container.querySelectorAll("video");

      videos.forEach((video) => {
        const item = video.closest(".user-pops-feed__item");

        const isActive =
          Number(item?.dataset.popIndex) === activeIndexRef.current;

        if (document.hidden || !isActive) {
          video.pause();
        } else {
          video.play().catch(() => {});
        }
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    watchProgressRef.current.forEach((progress, key) => {
      if (key !== String(activeId)) {
        watchProgressRef.current.set(key, {
          ...progress,
          lastTime: null,
        });
      }
    });
  }, [activeId]);

  useEffect(() => {
    const container = containerRef.current;
    const trigger = loadMoreRef.current;

    if (!container || !trigger || !hasMore || !initialized) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          !entries[0]?.isIntersecting ||
          loading ||
          loadingMore ||
          loadMorePendingRef.current
        ) {
          return;
        }

        loadMorePendingRef.current = true;

        Promise.resolve()
          .then(() => callbacksRef.current.loadMore())
          .catch((loadError) => {
            console.error("Unable to load more Pops:", loadError);
          })
          .finally(() => {
            loadMorePendingRef.current = false;
          });
      },
      {
        root: container,
        rootMargin: "800px 0px",
        threshold: 0,
      },
    );

    observer.observe(trigger);

    return () => observer.disconnect();
  }, [hasMore, popItems.length, loading, loadingMore, initialized]);

  const handleTimeUpdate = (event, pop, active) => {
    if (!active || document.hidden) return;

    const video = event.target;

    if (
      !(video instanceof HTMLVideoElement) ||
      video.paused ||
      video.seeking ||
      video.readyState < 2
    ) {
      return;
    }

    const popId = normalizePopId(pop.popId);

    if (!popId) return;

    const key = String(popId);
    const currentTime = Number(video.currentTime);

    if (!Number.isFinite(currentTime)) return;

    const previous = watchProgressRef.current.get(key) || {
      lastTime: null,
      watchSeconds: 0,
    };

    let watchSeconds = previous.watchSeconds;

    if (previous.lastTime !== null) {
      const delta = currentTime - previous.lastTime;

      if (delta > 0 && delta <= 1.5) {
        watchSeconds += delta;
      }
    }

    watchProgressRef.current.set(key, {
      lastTime: currentTime,
      watchSeconds,
    });

    if (
      watchSeconds < MIN_WATCH_SECONDS ||
      trackedViewsRef.current.has(key) ||
      pendingRecordedViewsRef.current.has(key)
    ) {
      return;
    }

    const duration = Number(video.duration);

    const completionPercent =
      Number.isFinite(duration) && duration > 0
        ? Math.min(100, (currentTime / duration) * 100)
        : 0;

    const stats = {
      watchSeconds: Number(watchSeconds.toFixed(2)),
      completionPercent: Number(completionPercent.toFixed(2)),
      referrerType: "feed",
    };

    pendingRecordedViewsRef.current.add(key);

    const generation = generationRef.current;

    Promise.resolve()
      .then(() => callbacksRef.current.recordPopView(popId, stats))
      .then(async (result) => {
        if (!result) {
          throw new Error("Unable to record Pop view.");
        }

        if (!mountedRef.current || generation !== generationRef.current) {
          return;
        }

        trackedViewsRef.current.add(key);

        const returnedCount =
          result.realViews ?? result.real_views ?? result.views;

        if (returnedCount !== undefined) {
          const realViews = normalizeCount(returnedCount);

          setViewCounts((previous) => {
            const existing = previous[key];

            const demoViews = normalizeCount(
              existing?.demoViews ?? pop.demoViews ?? pop.demo_views,
            );

            return {
              ...previous,
              [key]: {
                popId,
                realViews,
                demoViews,
                totalViews: realViews + demoViews,
              },
            };
          });

          return;
        }

        const results = await callbacksRef.current.getPopViewCounts([popId]);

        if (
          !mountedRef.current ||
          generation !== generationRef.current ||
          !Array.isArray(results)
        ) {
          return;
        }

        const normalized = results
          .map(normalizeViewResult)
          .find((item) => item?.popId === popId);

        if (normalized) {
          setViewCounts((previous) => ({
            ...previous,
            [key]: normalized,
          }));
        }
      })
      .catch((viewError) => {
        console.error("Pop view tracking failed:", viewError);
      })
      .finally(() => {
        pendingRecordedViewsRef.current.delete(key);
      });
  };

  const handleSavePop = async (pop) => {
    if (!normalizePopId(pop?.popId) || isPopSaving(pop.popId)) {
      return false;
    }

    return toggleSavePop(pop.popId);
  };

  const handleSeeked = (popId) => {
    const key = String(popId);
    const progress = watchProgressRef.current.get(key);

    if (!progress) return;

    watchProgressRef.current.set(key, {
      ...progress,
      lastTime: null,
    });
  };

  const handlePopClick = (index) => {
    if (index === activeIndexRef.current) return;

    const container = containerRef.current;
    const target = container?.querySelector(`[data-pop-index="${index}"]`);

    target?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const renderPop = (pop, index) => {
    const key = String(pop.popId);
    const active = index === activeIndex;

    const shouldRender = Math.abs(index - activeIndex) <= RENDER_DISTANCE;

    if (!shouldRender) {
      return (
        <section
          key={key}
          data-pop-id={key}
          data-pop-index={index}
          className="user-pops-feed__item"
        >
          <div className="user-pops-feed__placeholder" aria-hidden="true" />
        </section>
      );
    }

    const saved = isPopSaved(pop.popId);
    const saving = isPopSaving(pop.popId);
    const fetched = viewCounts[key];

    const fallbackRealViews = normalizeCount(
      pop.realViews ?? pop.real_views ?? pop.views,
    );

    const realViews = fetched
      ? fetched.realViews
      : getPopViewCount(pop.popId, fallbackRealViews);

    const demoViews = fetched
      ? fetched.demoViews
      : normalizeCount(pop.demoViews ?? pop.demo_views);

    const totalViews = fetched
      ? fetched.totalViews
      : normalizeCount(
          pop.totalViews ?? pop.total_views,
          realViews + demoViews,
        );

    const displayPop = {
      ...pop,
      views: totalViews,
      realViews,
      demoViews,
      totalViews,
      isSaved: saved,
    };

    return (
      <section
        key={key}
        data-pop-id={key}
        data-pop-index={index}
        className={`user-pops-feed__item ${
          active ? "user-pops-feed__item--active" : ""
        }`}
        onTimeUpdateCapture={(event) => {
          if (event.target?.tagName === "VIDEO") {
            handleTimeUpdate(event, displayPop, active);
          }
        }}
        onSeekedCapture={() => handleSeeked(pop.popId)}
      >
        <PopThumbnailCard
          pop={displayPop}
          mode="player"
          active={active}
          muted={muted}
          onMuteChange={setMuted}
          onClick={() => handlePopClick(index)}
          onSave={() => handleSavePop(pop)}
          isSaved={saved}
          saving={saving}
        />
      </section>
    );
  };

  if (loading && !popItems.length) {
    return (
      <div className="user-pops-feed">
        <div className="user-pops-feed__state" role="status">
          <LoaderCircle className="user-pops-feed__loader" size={26} />
          <span>Loading Pops...</span>
        </div>
      </div>
    );
  }

  if (errorMessage && !popItems.length) {
    return (
      <div className="user-pops-feed">
        <div className="user-pops-feed__state">
          <EmptyState
            icon={<VideoOff size={28} />}
            title="Unable to load Pops"
            description={errorMessage}
            action={
              <Button
                value="Try Again"
                icon={<RefreshCw size={16} />}
                variant="primary-outlined"
                onClick={refreshFeed}
              />
            }
          />
        </div>
      </div>
    );
  }

  if (!loading && !popItems.length) {
    return (
      <div className="user-pops-feed">
        <div className="user-pops-feed__state">
          <EmptyState
            icon={<VideoOff size={28} />}
            title="No Pops available"
            description="Check back for new sports moments."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="user-pops-feed">
      <div ref={containerRef} className="user-pops-feed__container">
        {popItems.map(renderPop)}

        {loadingMore && (
          <div className="user-pops-feed__load-more">
            <LoaderCircle className="user-pops-feed__loader" size={22} />
            <span>Loading more Pops...</span>
          </div>
        )}

        {hasMore && (
          <div
            ref={loadMoreRef}
            className="user-pops-feed__load-trigger"
            aria-hidden="true"
          />
        )}

        {!hasMore && popItems.length > 0 && (
          <div className="user-pops-feed__end">You're all caught up.</div>
        )}
      </div>
    </div>
  );
}
